import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { cambiarEstadoDeCuenta, consultarUsoDeCuenta, obtenerCuenta } from '../api/cuentasApi.js';
import { importe } from '../formato.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

/**
 * Desactivar y reactivar una cuenta (RF-039, D-22, RN-069, RN-058).
 *
 * EL ORDEN DE LAS PUERTAS NO ES NEGOCIABLE, y esta pantalla lo refleja:
 *   1. BLOQUEO DURO POR SALDO, en las cuatro clases de cuenta. Si el saldo —o la
 *      deuda— no es exactamente 0.00, se rechaza con CUENTA_CON_SALDO y
 *      <b>confirmado=true no lo salta</b>. Ni siquiera se llega a pedir la
 *      confirmación (RN-069a). Aquí se ANTICIPA con `puedeDesactivarse`, que es
 *      AYUDA VISUAL: el bloqueo real lo aplica el servidor.
 *   2. Solo superado eso, la confirmación con el uso desglosado por persona.
 *
 * Reactivar es INMEDIATO: no pide confirmación y no consulta nada (RN-042).
 */
export default function DesactivarCuenta() {
  const { idCuenta } = useParams();
  const navegar = useNavigate();

  const [cuenta, setCuenta] = useState(null);
  const [uso, setUso] = useState(null);
  const [entendido, setEntendido] = useState(false);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    obtenerCuenta(idCuenta)
      .then((datos) => {
        setCuenta(datos);
        if (datos.activo && datos.puedeDesactivarse) {
          consultarUsoDeCuenta(idCuenta).then(setUso).catch(() => setUso(null));
        }
      })
      .catch(setError);
  }, [idCuenta]);

  async function ejecutar(activo) {
    setError(null);
    setOcupado(true);
    try {
      await guardarConConexion(() => cambiarEstadoDeCuenta(idCuenta, {
        activo,
        confirmado: !activo,
        version: cuenta.version,
      }));
      // RF-148, RN-391: REEMPLAZA el paso del formulario, no lo deja debajo.
      navegar('/cuentas', { replace: true });
    } catch (fallo) {
      setError(fallo);
    } finally {
      setOcupado(false);
    }
  }

  if (!cuenta) {
    return (
      <Shell titulo="Estado de la cuenta" volverA="/cuentas">
        {error ? <AvisoDeError error={error} /> : <div className="aviso i">Cargando…</div>}
      </Shell>
    );
  }

  const esTarjeta = cuenta.esCredito;
  const subtitulo = `${cuenta.nombre} · ${cuenta.nombreTipoCuenta.toLowerCase()} · ${cuenta.codigoISO}`;

  // --- Reactivar ---------------------------------------------------------
  if (!cuenta.activo) {
    return (
      <Shell titulo="Reactivar cuenta" subtitulo={subtitulo} volverA={`/cuentas/${idCuenta}`}>
        <AvisoDeError error={error} />

        {/* Fila 2 del inventario del incremento 15 (RF-222): salió la justificación del
            diseño («no consulta nada: no es una operación destructiva»). */}
        <div className="aviso i" data-prueba="reactivar-ayuda">
          <b>Reactivar es inmediato.</b> La cuenta vuelve a ofrecerse en la captura, con todo su
          historial, y vuelve a su lugar alfabético en su bloque de moneda.
        </div>

        {/* Fila 3 del inventario del incremento 15 (RF-223): salió el código de catálogo
            MONEDA_INACTIVA, que solo es lícito al final del aviso de error (RN-333). */}
        <div className="aviso w" data-prueba="reactivar-rechazo">
          <b>Único caso en que se rechaza:</b> si su moneda fue desactivada mientras la cuenta estaba
          inactiva. Primero reactiva la moneda en Ajustes › Monedas.
        </div>

        <div style={{ display: 'grid', gap: 8, margin: 12 }}>
          <button className="btn" type="button" disabled={ocupado} onClick={() => ejecutar(true)}>Reactivar cuenta</button>
          <Link className="btn sec" to="/cuentas">Cancelar</Link>
        </div>
      </Shell>
    );
  }

  // --- Bloqueo duro por saldo (D-22, RN-069) -----------------------------
  if (!cuenta.puedeDesactivarse) {
    const valor = esTarjeta ? cuenta.deuda : cuenta.saldo;
    const aFavor = Number(valor ?? 0) < 0;

    return (
      <Shell titulo={`Desactivar ${esTarjeta ? 'tarjeta' : 'cuenta'}`} subtitulo={subtitulo} volverA={`/cuentas/${idCuenta}`}>
        <div className="aviso e">
          <b>
            ✖ No puedes desactivar esta {esTarjeta ? 'tarjeta' : 'cuenta'}:
            {aFavor
              ? ` tiene ${importe(String(Math.abs(Number(valor))), cuenta.codigoISO)} a tu favor.`
              : ` todavía ${esTarjeta ? 'debe' : 'tiene'} ${importe(valor, cuenta.codigoISO)}.`}
          </b>
          {' '}
          {esTarjeta && !aFavor ? 'Regístrale el pago y vuelve a intentarlo.' : 'Déjala en cero y vuelve a intentarlo.'}
          {cuenta.cantidadMovimientos === 0 && ' También puedes corregir su saldo inicial a cero si la capturaste por error.'}
          {/* Fila 4 del inventario del incremento 15 (RF-222/RF-223, D-325): salió el
              sub-bloque de ayuda completo, que citaba el código de catálogo CUENTA_CON_SALDO,
              el estado HTTP 409 y justificaba el diseño («y es deliberado»). Su único matiz
              útil se reubicó como frase condicional en la tarjeta de abajo (RN-632, D-328). */}
        </div>

        <div className="card lista" data-prueba="por-que-esta-bloqueada">
          <h3>Por qué está bloqueada</h3>
          <div className="fila"><span className="crece">{esTarjeta ? 'Deuda calculada' : 'Saldo calculado'}</span><b>{importe(valor, cuenta.codigoISO)}</b></div>
          <div className="fila"><span className="crece">Se permite la baja únicamente con</span><b>{importe('0.00', cuenta.codigoISO)} exacto</b></div>
          <div className="fila">
            <span className="crece">Movimientos de la cuenta</span>
            <b>{cuenta.cantidadMovimientos} · {cuenta.saldoInicialEditable ? 'saldo inicial corregible' : 'saldo inicial congelado'}</b>
          </div>
          {/* RN-632 · D-328 · CA-15-09 · La frase es CONDICIONAL: solo cuando el valor
              calculado es a favor (negativo). Con dinero en la cuenta o con deuda —el caso
              de todos los días— no se dibuja, para no hablarle de un caso que no es el suyo. */}
          {aFavor && (
            <p className="ayuda" data-prueba="desactivar-saldo-a-favor">
              Un saldo a favor también bloquea la baja.
            </p>
          )}
        </div>

        {cuenta.cantidadMovimientos === 0 ? (
          <div className="aviso w">
            <b>La vía de salida.</b> Para desactivar una cuenta, su saldo debe quedar en cero.
            Puedes corregir el saldo inicial o registrar los movimientos que falten. Esta cuenta
            <b>no tiene movimientos</b>, así que su saldo inicial todavía se puede corregir: ponlo
            en cero y la baja procede.
          </div>
        ) : (
          <div className="aviso i">
            <b>Esto solo bloquea la BAJA.</b> Con saldo puedes seguir <b>editando</b> la cuenta,
            <b> renovando</b> el plástico y capturando con ella. Lo que no se puede es
            archivarla con dinero dentro.
          </div>
        )}

        <div style={{ display: 'grid', gap: 8, margin: 12 }}>
          {cuenta.cantidadMovimientos === 0 && (
            <Link className="btn" to={`/cuentas/${idCuenta}/editar?cero=1`}>Corregir el saldo inicial a cero</Link>
          )}
          <Link className="btn sec" to={`/cuentas/${idCuenta}`}>Volver a la cuenta</Link>
        </div>
      </Shell>
    );
  }

  // --- Confirmación ordinaria (RN-058) -----------------------------------
  return (
    <Shell titulo={`Desactivar ${esTarjeta ? 'tarjeta' : 'cuenta'}`} subtitulo={subtitulo} volverA={`/cuentas/${idCuenta}`}>
      <AvisoDeError error={error} />

      <div className="aviso ok">
        <b>✔ Esta cuenta está en cero: la baja procede.</b> El saldo calculado es exactamente
        {' '}{importe('0.00', cuenta.codigoISO)}, comparado a dos decimales. Superado ese
        bloqueo, la baja pide confirmación.
      </div>

      <div className="card lista">
        <h3>Qué usa esta cuenta hoy</h3>
        {uso?.cantidadRegistros === -1 ? (
          <div className="ayuda">No se pudo verificar el uso; el conteo está incompleto.</div>
        ) : (
          <>
            <div className="fila"><span className="crece">Movimientos que la referencian</span><b>{uso?.cantidadRegistros ?? 0}</b></div>
            {(uso?.cantidadPorUsuario ?? []).map((persona) => (
              <div key={persona.idUsuario} className="fila">
                <span className="crece">Movimientos de {persona.nombre}</span><b>{persona.cantidad}</b>
              </div>
            ))}
            <div className="fila"><span className="crece">Saldo calculado</span><b>{importe('0.00', cuenta.codigoISO)} · en cero</b></div>
          </>
        )}
      </div>

      <div className="aviso i">
        <b>Qué pasa si la desactivas.</b> Deja de ofrecerse al capturar —ni como origen ni como
        destino de un traspaso—, pero <b>su historial se conserva íntegro</b> y sigue apareciendo en
        el detalle de los movimientos que ya la referencian y en los reportes del periodo en que se
        usó. <b>No se borra nada</b> y puedes reactivarla cuando quieras. En la
        lista <b>dejará de verse</b> hasta que actives “Ver desactivadas”.
      </div>

      <div style={{ margin: 12 }}>
        <label>
          <input type="checkbox" checked={entendido} onChange={(e) => setEntendido(e.target.checked)} />
          {' '}Entiendo que dejará de ofrecerse al capturar y que sus movimientos se conservan.
        </label>
      </div>

      <div style={{ display: 'grid', gap: 8, margin: 12 }}>
        <button className="btn" type="button" disabled={!entendido || ocupado} onClick={() => ejecutar(false)}>
          Desactivar {esTarjeta ? 'tarjeta' : 'cuenta'}
        </button>
        <Link className="btn sec" to={`/cuentas/${idCuenta}`}>Cancelar</Link>
      </div>
    </Shell>
  );
}
